import { useEffect, useLayoutEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { parseMoney } from '@pos/core';
import { cx } from './index.tsx';

const plain = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** 150050 → "1.500,5"; 150000 → "1.500". */
export function centsToText(cents: number | null): string {
  return cents == null ? '' : plain.format(cents / 100);
}

/** Flechas que recorren los montos sugeridos: → y ↓ al siguiente (mayor), ← y ↑ al anterior. */
const PRESET_STEPS: Record<string, 1 | -1> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/**
 * El monto sugerido (de una lista de menor a mayor) que sigue a `current`: el primero mayor
 * (dir 1) o el último menor (dir -1); null si no hay. Sin monto, hacia adelante va al primero.
 */
export function stepPreset(presets: readonly number[], current: number | null, dir: 1 | -1): number | null {
  if (current == null) return dir === 1 ? (presets[0] ?? null) : null;
  return (dir === 1 ? presets.find((p) => p > current) : presets.findLast((p) => p < current)) ?? null;
}

/**
 * Campo de importe en pesos. Acepta "1500", "1.500,50" o "1500.5" (teclado de celular),
 * y devuelve centavos enteros (o null si está vacío o es inválido).
 */
export function MoneyInput({
  value,
  onChange,
  className,
  invalid,
  presets,
  onKeyDown,
  ...rest
}: {
  value: number | null;
  onChange: (cents: number | null, raw: string) => void;
  invalid?: boolean;
  /** Montos sugeridos, de menor a mayor: con las flechas se pasa de uno a otro. */
  presets?: readonly number[];
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [text, setText] = useState(() => centsToText(value));
  const input = useRef<HTMLInputElement>(null);
  const focused = useRef(false);
  const selectAll = useRef(false);

  // Si el valor cambia desde afuera (p. ej. "calcular con recargo"), se refleja. Mientras se escribe
  // no, para no pisar lo tipeado; salvo con montos sugeridos: elegir uno no saca el foco del campo.
  useEffect(() => {
    if (!focused.current) setText(centsToText(value));
    else if (presets && value !== parseMoney(text)) {
      setText(centsToText(value));
      selectAll.current = true;
    }
  }, [value]);

  // El monto elegido queda marcado: si después se escribe otro, lo reemplaza.
  useLayoutEffect(() => {
    if (!selectAll.current) return;
    selectAll.current = false;
    input.current?.select();
  });

  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-500">$</span>
      <input
        ref={input}
        inputMode="decimal"
        autoComplete="off"
        data-autofocus={rest.autoFocus || undefined}
        className={cx(
          'block h-11 w-full rounded-lg border-0 bg-white pr-3 pl-7 text-base text-slate-900 ring-1 placeholder:text-slate-400',
          'focus:ring-2 focus:ring-brand focus:outline-none disabled:bg-slate-100 sm:text-sm',
          invalid ? 'ring-red-400' : 'ring-slate-300',
          className,
        )}
        value={text}
        onFocus={() => (focused.current = true)}
        onBlur={() => {
          focused.current = false;
          const parsed = parseMoney(text);
          if (parsed !== null) setText(centsToText(parsed));
        }}
        onChange={(e) => {
          setText(e.target.value);
          onChange(e.target.value.trim() === '' ? null : parseMoney(e.target.value), e.target.value);
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          const dir = PRESET_STEPS[e.key];
          if (!presets?.length || !dir || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
          e.preventDefault();
          const next = stepPreset(presets, value, dir);
          if (next == null) return;
          const t = centsToText(next);
          setText(t);
          selectAll.current = true;
          onChange(next, t);
        }}
        {...rest}
      />
    </div>
  );
}
